<template>
  <ModalDialog
    ref="dialog"
    title="Add Credit Note"
    :submitting="submitting"
    :submitDisabled="submitting || !canSubmitCreditNote"
    submitTitle="Add Credit Note"
    icon="fa-money-check-alt"
    :scrollable="true"
    @closed="onClosed"
    @submitted="onSubmitted">

    <LoadingPanel :loading="loading" :loadingError="loadingError" />
    <FormGroup v-bind:form="dialogForm">
      <FormRow label="Contact">
        <ContactSearchableSelect :isCustomer="true" :activeOnly="true" v-model="contactId" field="contactId" />
      </FormRow>
      <div class="add-credit-note-container">

        <p v-if="!contactId" class="text-center text-muted font-italic py-4">Select a contact to add a credit note</p>

        <div v-if="!loading && !loadingError && contactId">
          <FormRow label="Order">
            <OrderSearchableSelect :contactId="contactId" v-model="creditNote.orderId" field="orderId" note="Order the credit note will be associated with" placeholder="Enter search text to find an order" />
          </FormRow>
          
          <FormRow label="Labels">
            <FormLabelTag popupName="Credit Note Labels" v-model="creditNote.labelList" labelType="creditNote" field="labelList" />
          </FormRow>
          <FormRow label="Description">
            <FormInputText v-model="creditNote.description" field="description" />
          </FormRow>
          <FormRow label="Amount" :fieldCount="2">
            <template v-slot:field1>
              <FormInputCurrency ref="creditAmount" v-model="creditNote.amount" field="amount" note="Tax exclusive" />
            </template>
            <template v-slot:field2>
              <FormSelect v-model="creditNote.taxRateId" :options="taxRateLookup" field="taxRateId" />
            </template>
          </FormRow>
          <FormRow label="Total Credit Amount">
            <FormStaticLabel><strong>{{currentAmount | formatCurrency}}</strong></FormStaticLabel>
          </FormRow>

          <FormHorizontalRule class="my-2" />
          <TabbedBox>
            <template v-slot:tablist>
              <Tab :first="true" tabid="tab-allocations-invoice">
                <i class="far fa-envelope-open-dollar"></i> Invoice Allocations
              </Tab>
              <Tab tabid="tab-allocations-orders">
                <i class="far fa-shopping-cart"></i> Order Allocations
              </Tab>
            </template>
            <template v-slot:tabcontent>
              <TabPage :first="true" tabid="tab-allocations-invoice">
                <InvoiceAllocation
                  :contactId="contactId"
                  :defaultInvoiceId="invoiceId"
                  :isFixedAmount="true"
                  :paymentAmount="currentAmount"
                  v-model="availableInvoiceList"
                  @updatePayment="updatePaymentFromAllocation($event)" />
              </TabPage>
              <TabPage tabid="tab-allocations-orders">
                <OrderAllocationList
                  :contactId="contactId"
                  :paymentAmount="currentAmount"
                  :isFixedAmount="true"
                  v-model="availableOrderList"
                />
              </TabPage>
            </template>
          </TabbedBox>        
        </div>
      </div>
    </FormGroup>
  </ModalDialog>
</template>

<style scoped>
.add-credit-note-container {
  min-height: 300px;
}
</style>

<script>
import _ from 'lodash'
import moment from 'moment'
import Utils from '@/utils/utils'
import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'

import ContactSearchableSelect from '@/modules/contacts/components/contactSearchableSelect'
import OrderSearchableSelect from '@/modules/orders/components/orderSearchableSelect'
import InvoiceAllocation from '@/modules/accounts/components/invoiceAllocation'
import OrderAllocationList from '@/modules/accounts/components/orderAllocationList'

import { InvoiceStatusTypes } from '@/store/types'

export default {
  name: 'AddPaymentDialog',
  mixins: [
    ModalDialogMixin,
    form
  ],
  components: {
    ModalDialog,
    ContactSearchableSelect,
    OrderSearchableSelect,
    InvoiceAllocation,
    OrderAllocationList
  },
  props: {
  },
  data() {
    return {
      dialogForm: {},
      creditNote: { },
      availableInvoiceList: [],
      availableOrderList: [],
      invoiceId: null,
      contactId: null,
      contactData: {},
      loading: false,
      loadingError: '',
      submitting: false,

      taxRateLookup: []
    }
  },
  created() {
  },
  watch: {
    contactId: function() {
      this.requestData()
    }
  },
  mounted() {
  },
  computed: {
    canSubmitCreditNote() {
      if(!this.contactId || !this.creditNote.orderId)
        return false
      if(this.currentAmount < this.totalInvoiceAllocation + this.totalOrderAllocation)
        return false
      if(this.totalInvoiceErrorCount > 0 || this.totalOrderErrorCount > 0)
        return false
      if(this.currentAmount == 0)
        return false

      return true
    },
    taxRateValue() {
      let taxRate = this.taxRateLookup.find(taxRate => taxRate.value == this.creditNote.taxRateId)
      if(taxRate)
        return taxRate.rate
      return 0
    },
    currentAmount() {
      // Apply selected tax rate
      return Utils.roundCurrency(this.parseDecimal(this.creditNote.amount) * ((100.0 + this.taxRateValue) / 100.0))
    },
    totalInvoiceAllocation() {
      return this.availableInvoiceList.reduce((curVal, invoice) => {
        return Utils.roundCurrency(curVal + this.parseDecimal(invoice.allocatedAmount))
      }, 0)
    },
    totalOverpaymentValue() {
      // Note: Don't include orders. They are just reserved amounts
      let overAmount = this.currentAmount - this.totalInvoiceAllocation
      return Math.max(overAmount, 0)
    },
    totalInvoiceErrorCount() {
      return this.availableInvoiceList.filter(invoice => this.parseDecimal(invoice.allocatedAmount) > invoice.amountDue).length
    },
    totalOrderErrorCount() {
      return this.availableOrderList.filter(order => this.parseDecimal(order.allocatedAmount) > order.amountDue).length
    },
    totalOrderAllocation() {
      return this.availableOrderList.reduce((curVal, order) => {
        return Utils.roundCurrency(curVal + this.parseDecimal(order.allocatedAmount))
      }, 0)
    }
  },
  methods: {
    show(options = {}) {
      // Reset form
      this.responseInfo = { }
      this.dialogForm = { }

      this.contactId = options ? options.contactId : null
      this.invoiceId = options ? options.invoiceId : null    // If crediting a specific invoice

      // Defaults
      this.creditNote = {
        amount: 0,
        orderId: null,
        description: '',
        taxRateId: null,
        labelList: []
      }

      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(false)
    },
    onSubmitted() {
      let invoiceCount = this.availableInvoiceList.filter(invoice => this.parseDecimal(invoice.allocatedAmount) > 0).length
      let orderCount = this.availableOrderList.filter(order => this.parseDecimal(order.allocatedAmount) > 0).length
      let paymentDisplay = Utils.formatCurrency(this.currentAmount)
      let remainingAmount = this.totalOverpaymentValue - this.totalOrderAllocation
      let remainingPaymentDisplay = Utils.formatCurrency(remainingAmount)
      let contactName = this.contactData.name
      
      var message = `Confirm credit note of <strong>${paymentDisplay}</strong> applied to <strong>${contactName}</strong>?`
      if(remainingAmount > 0 && (invoiceCount > 0 || orderCount > 0))
        message += `<br /><br /><strong>Note</strong>: Additional credit of <strong>${remainingPaymentDisplay}</strong> will be added for future!`
      this.confirmMessage('Confirm Credit Note?', null, { html: message })
      .then(success => {
        if(success) {
          this._submitCreditNote()
        }
      })
    },
    _submitCreditNote() {
      var createCreditNoteData = {
        contactId: this.contactId,
        orderId: this.creditNote.orderId,
        amount: Utils.roundNumber(this.parseDecimal(this.creditNote.amount), 4),    // Tax exclusive. Rounded to 4 decimal places to allow tax adjustments
        taxRateId: this.creditNote.taxRateId,
        additionalCredit: Utils.roundCurrency(this.totalOverpaymentValue),    // Tax inclusive
        description: this.creditNote.description,
        labelList: this.creditNote.labelList.map(label => label.id)
      }

      createCreditNoteData.invoiceAllocationList = this.availableInvoiceList.filter(invoice => this.parseDecimal(invoice.allocatedAmount) > 0).map(invoice => {
        return {
          invoiceId: invoice.id,
          amount: Utils.roundCurrency(this.parseDecimal(invoice.allocatedAmount))
        }
      })
      createCreditNoteData.orderAllocationList = this.availableOrderList.filter(order => this.parseDecimal(order.allocatedAmount) > 0).map(order => {
        return {
          orderId: order.id,
          amount: Utils.roundCurrency(this.parseDecimal(order.allocatedAmount))
        }
      })

      this.submitting = true
      this.$apiService.accountsService.createCreditNote(createCreditNoteData)
      .then(response => {
        this.modalClosed(response.data.id)
        this.hide()
      })
      .catch(err => {
        this.$data.dialogForm = this.processFormResponse(err)
      })
      .finally(() => {
        this.$data.submitting = false
      })
    },

    requestData() {
      this.contactData = {}
      this.loading = true
      this.loadingError = ''
      this.taxRateLookup = []

      Promise.all([
        this.$apiService.contactService.getContact(this.contactId),
        this.$apiService.adminService.getAccount(),
        this.$apiService.adminService.getTaxRateList()
      ])
      .then(results => {
        this.contactData = results[0].data
        this.creditNote.taxRateId = results[1].data.salesTaxRateId

        this.taxRateLookup = results[2].data.map(taxRate => {
          return { value: taxRate.id, name: taxRate.displayName, code: taxRate.code, rate: taxRate.rate }
        })
      })
      .catch(err => {
        this.loadingError = this.processFormResponse(err).errorMessage
      })
      .finally(() => {
        this.loading = false
      })
    },

    updatePaymentFromAllocation(invoiceAmount) {
      // Reverse calc based on tax rate to get the tax exclusive amount to pay
      let taxChange = (1.0+this.taxRateValue)
      if(taxChange > 0)
        this.creditNote.amount = Utils.roundCurrency(invoiceAmount - (invoiceAmount / taxChange))
      else
        this.creditNote.amount = invoiceAmount
    }
  }
}
</script>